import { ProductSalesByLocationMachine } from '../../services/openapi';

export type MachineGroup = {
    machineKey: string;
    machineDescription: string;
    rows: ProductSalesByLocationMachine[];
    totalQuantity: number;
    totalAmountInCents: number;
};

export type LocationGroup = {
    locationKey: string;
    locationDescription: string;
    locationSortKey: number;
    siteId: string | null;
    // The site's Description, else its id
    siteLabel: string;
    machines: MachineGroup[];
    totalQuantity: number;
    totalAmountInCents: number;
};

export type ProductTotal = {
    productKey: string;
    productDescription: string;
    totalQuantity: number;
    totalAmountInCents: number;
};

export type ProductSalesReportData = {
    locationGroups: LocationGroup[];
    productTotals: ProductTotal[];
    grandTotalQuantity: number;
    grandTotalAmountInCents: number;
    showSiteId: boolean;
};

export const formatQuantity = (value: number) => (value ?? 0).toLocaleString('nl-nl');

export const formatAmount = (amountInCents: number) => ((amountInCents ?? 0) / 100).toLocaleString('nl-nl', { style: 'currency', currency: 'EUR' });

// Nulls (e.g. no description available) sort after everything else, alphabetically otherwise.
export const compareText = (a: string | null, b: string | null) => {
    if (a === b) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return a.localeCompare(b);
};

export const buildProductSalesReport = (rows: ProductSalesByLocationMachine[], unknownLabel: string): ProductSalesReportData => {
    const locationIndex = new Map<string, LocationGroup>();
    const locationGroups: LocationGroup[] = [];

    const productTotalsIndex = new Map<string, ProductTotal>();

    rows.forEach((row) => {
        // A real location belongs to exactly one site, so its id alone is a safe group key.
        // Machines with no location linked aren't tied to a location record at all, so
        // rows for different sites must not be folded into one shared "no location" group.
        const locationKey = row.locationId !== null ? `id:${row.locationId}` : `no-location:${row.siteId ?? 'unknown-site'}`;
        const machineKey = row.machineLocationId ?? 'unknown-machine';
        const productKey = row.productId !== null ? `id:${row.productId}` : `desc:${row.productDescription ?? 'unknown-product'}`;

        let location = locationIndex.get(locationKey);
        if (!location) {
            location = {
                locationKey,
                locationDescription: row.locationDescription ?? unknownLabel,
                locationSortKey: row.locationSortKey,
                siteId: row.siteId,
                siteLabel: row.siteDescription || row.siteId || unknownLabel,
                machines: [],
                totalQuantity: 0,
                totalAmountInCents: 0
            };
            locationIndex.set(locationKey, location);
            locationGroups.push(location);
        }

        let machine = location.machines.find(m => m.machineKey === machineKey);
        if (!machine) {
            machine = { machineKey, machineDescription: row.machineDescription ?? unknownLabel, rows: [], totalQuantity: 0, totalAmountInCents: 0 };
            location.machines.push(machine);
        }

        machine.rows.push(row);
        machine.totalQuantity += row.totalQuantity;
        machine.totalAmountInCents += row.totalAmountInCents;
        location.totalQuantity += row.totalQuantity;
        location.totalAmountInCents += row.totalAmountInCents;

        let productTotal = productTotalsIndex.get(productKey);
        if (!productTotal) {
            productTotal = { productKey, productDescription: row.productDescription ?? unknownLabel, totalQuantity: 0, totalAmountInCents: 0 };
            productTotalsIndex.set(productKey, productTotal);
        }
        productTotal.totalQuantity += row.totalQuantity;
        productTotal.totalAmountInCents += row.totalAmountInCents;
    });

    // Sites are only worth calling out (in the table and the PDF) when the result actually spans more than one.
    // Then each site's locations are kept together, so the site can be set apart.
    const showSiteId = new Set(rows.map(row => row.siteId)).size > 1;

    locationGroups.sort((a, b) =>
        (showSiteId ? compareText(a.siteLabel, b.siteLabel) || compareText(a.siteId, b.siteId) : 0)
        || a.locationSortKey - b.locationSortKey || compareText(a.locationDescription, b.locationDescription) || compareText(a.siteId, b.siteId));
    locationGroups.forEach(location => {
        location.machines.sort((a, b) => compareText(a.machineDescription, b.machineDescription));
        location.machines.forEach(machine => {
            machine.rows.sort((a, b) => compareText(a.productDescription, b.productDescription));
        });
    });

    const productTotals = Array.from(productTotalsIndex.values()).sort((a, b) => compareText(a.productDescription, b.productDescription));

    const grandTotalQuantity = productTotals.reduce((sum, p) => sum + p.totalQuantity, 0);
    const grandTotalAmountInCents = productTotals.reduce((sum, p) => sum + p.totalAmountInCents, 0);

    return { locationGroups, productTotals, grandTotalQuantity, grandTotalAmountInCents, showSiteId };
};
